import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Card, CardContent, Typography, TextField, Button, Alert, CircularProgress } from '@mui/material';
import { LockReset } from '@mui/icons-material';
import apiService from '../services/api';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { setProfile } from '../store/slices/authSlice';

export default function ChangePassword() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const mustChange = useAppSelector(s => s.auth.mustChangePassword);
  const isAdmin = useAppSelector(s => s.auth.isAdmin);
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async () => {
    setError(null);
    if (next.length < 8) { setError('New password must be at least 8 characters.'); return; }
    if (next !== confirm) { setError('New password and confirmation do not match.'); return; }
    setBusy(true);
    try {
      await apiService.changePassword(current, next);
      dispatch(setProfile({ isAdmin, mustChangePassword: false }));
      setDone(true);
      setTimeout(() => navigate('/'), 1200);
    } catch (e: any) {
      setError(e.response?.data?.detail || e.message || 'Failed to change password');
    } finally { setBusy(false); }
  };

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', p: 2 }}>
      <Card sx={{ maxWidth: 440, width: '100%' }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
            <LockReset color="primary" />
            <Typography variant="h5" fontWeight="bold">Set a new password</Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {mustChange
              ? 'Your account uses a temporary password. Choose your own password to continue.'
              : 'Update your account password.'}
          </Typography>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {done && <Alert severity="success" sx={{ mb: 2 }}>Password updated — redirecting…</Alert>}

          <TextField label="Current / temporary password" type="password" fullWidth margin="normal"
            value={current} onChange={e => setCurrent(e.target.value)} autoFocus />
          <TextField label="New password" type="password" fullWidth margin="normal"
            value={next} onChange={e => setNext(e.target.value)} helperText="At least 8 characters" />
          <TextField label="Confirm new password" type="password" fullWidth margin="normal"
            value={confirm} onChange={e => setConfirm(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && submit()} />

          <Button variant="contained" fullWidth size="large" sx={{ mt: 3 }}
            disabled={busy || done || !current || !next || !confirm} onClick={submit}>
            {busy ? <CircularProgress size={22} /> : 'Change password'}
          </Button>
        </CardContent>
      </Card>
    </Box>
  );
}
